import httpClient from "../../../../shared/api/httpClient";

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * List paginated treasury forms with optional filters.
 * @param {Object} options
 * @param {number} [options.page=1]
 * @param {number} [options.limit=10]
 * @param {string} [options.name]
 * @param {string} [options.type]
 * @param {string} [options.status]
 * @param {AbortSignal} [options.signal]
 */
export async function listTreasuryForms({
  page = 1,
  limit = 10,
  name,
  type,
  status,
  signal,
} = {}) {
  const query = buildQuery({
    page,
    limit,
    name: name ? String(name).trim() : undefined,
    type: type || undefined,
    status: status || undefined,
  });

  return httpClient.get(`/treasury/forms${query}`, { signal });
}

/**
 * Get detailed treasury form by ID (including current version and fields).
 * @param {number|string} formId
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function getTreasuryFormById(formId, { signal } = {}) {
  return httpClient.get(`/treasury/forms/${formId}`, { signal });
}

/** Create a treasury form and its first version. */
export async function createTreasuryForm(payload) {
  return httpClient.post("/treasury/forms", payload);
}

/** Update metadata and, when supplied, create a new structural version. */
export async function updateTreasuryForm(formId, payload) {
  return httpClient.patch(`/treasury/forms/${formId}`, payload);
}

/** Return whether a form has any historical assignment. */
export async function treasuryFormHasAssignments(formId, { signal } = {}) {
  const response = await httpClient.get(
    `/treasury/forms/${formId}/assignments?page=1&limit=1`,
    { signal }
  );
  return Number(response?.meta?.totalItems || 0) > 0;
}

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function normalizeMovementScope(value) {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "EXPENSE" || normalized === "EGRESO") return "EXPENSE";
  if (normalized === "BOTH" || normalized === "AMBOS") return "BOTH";
  return "INCOME";
}

/** Load current and historical accounts so old integrations remain readable. */
export async function listTreasuryFormAccounts({ signal } = {}) {
  const response = await httpClient.get("/treasury/accounts/history", { signal });
  return unwrapList(response).map((account) => ({
    id: account.id,
    name: account.name ?? account.nombre ?? `Cuenta #${account.id}`,
    currency: account.currency ?? account.moneda ?? "ARS",
    isActive: account.isActive ?? account.is_active ?? account.active ?? true,
    deletedAt: account.deletedAt ?? account.deleted_at ?? null,
  }));
}

/** Load categories, including historical ones used by existing versions. */
export async function listTreasuryFormCategories({ signal } = {}) {
  const response = await httpClient.get(
    "/treasury/movement-categories?include_deleted=true",
    { signal }
  );
  return unwrapList(response).map((category) => ({
    id: category.id,
    name: category.name ?? category.nombre ?? `Categoría #${category.id}`,
    movementTypeScope: normalizeMovementScope(
      category.movementTypeScope ??
        category.movement_type_scope ??
        category.type ??
        category.tipo
    ),
    isActive: category.isActive ?? category.is_active ?? category.active ?? true,
    deletedAt: category.deletedAt ?? category.deleted_at ?? null,
  }));
}

/**
 * Archive a treasury form logically.
 * @param {number|string} formId
 */
export async function archiveTreasuryForm(formId) {
  return httpClient.patch(`/treasury/forms/${formId}/archive`);
}

/**
 * Restore an archived treasury form.
 * @param {number|string} formId
 */
export async function restoreTreasuryForm(formId) {
  return httpClient.patch(`/treasury/forms/${formId}/restore`);
}

/**
 * List eligible active employees for assignment to a form.
 * @param {number|string} formId
 * @param {Object} [options]
 * @param {number} [options.page=1]
 * @param {number} [options.limit=50]
 * @param {string} [options.search]
 * @param {AbortSignal} [options.signal]
 */
export async function listTreasuryFormEmployeeOptions(
  formId,
  { page = 1, limit = 50, search, signal } = {}
) {
  const query = buildQuery({
    page,
    limit,
    search: search ? String(search).trim() : undefined,
  });
  return httpClient.get(`/treasury/forms/${formId}/employee-options${query}`, { signal });
}

/**
 * Assign a treasury form in batch to employees.
 * @param {number|string} formId
 * @param {Object} payload
 * @param {number[]} payload.employeeIds
 * @param {string} [payload.sector]
 * @param {string} payload.operationalDate
 */
export async function createTreasuryFormAssignments(formId, payload) {
  return httpClient.post(`/treasury/forms/${formId}/assignments`, payload);
}

/**
 * List paginated assignments for a form with optional filters.
 * @param {number|string} formId
 * @param {Object} [options]
 * @param {number} [options.page=1]
 * @param {number} [options.limit=10]
 * @param {string} [options.status]
 * @param {number} [options.employeeId]
 * @param {AbortSignal} [options.signal]
 */
export async function listTreasuryFormAssignments(
  formId,
  { page = 1, limit = 10, status, employeeId, signal } = {}
) {
  const query = buildQuery({
    page,
    limit,
    status: status || undefined,
    employeeId: employeeId || undefined,
  });
  return httpClient.get(`/treasury/forms/${formId}/assignments${query}`, { signal });
}

/**
 * Cancel a pending assignment.
 * @param {number|string} assignmentId
 */
export async function cancelTreasuryFormAssignment(assignmentId) {
  return httpClient.patch(`/treasury/form-assignments/${assignmentId}/cancel`, {});
}

/**
 * Get response details and submitted answers of an assignment.
 * @param {number|string} assignmentId
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function getTreasuryFormAssignmentResponse(assignmentId, { signal } = {}) {
  return httpClient.get(`/treasury/form-assignments/${assignmentId}/response`, { signal });
}

/**
 * List paginated submissions across all forms with comprehensive filters.
 * @param {Object} options
 * @param {number} [options.page=1]
 * @param {number} [options.limit=10]
 * @param {number} [options.formId]
 * @param {number} [options.employeeId]
 * @param {string} [options.sector]
 * @param {string} [options.operationalDate]
 * @param {string} [options.status]
 * @param {number} [options.periodId]
 * @param {string} [options.search]
 * @param {AbortSignal} [options.signal]
 */
export async function listTreasuryFormSubmissions({
  page = 1,
  limit = 10,
  formId,
  employeeId,
  sector,
  operationalDate,
  status,
  periodId,
  search,
  signal,
} = {}) {
  const query = buildQuery({
    page,
    limit,
    formId: formId || undefined,
    employeeId: employeeId || undefined,
    sector: sector ? String(sector).trim() : undefined,
    operationalDate: operationalDate || undefined,
    status: status || undefined,
    periodId: periodId || undefined,
    search: search ? String(search).trim() : undefined,
  });
  return httpClient.get(`/treasury/form-submissions${query}`, { signal });
}

/**
 * Get distinct filter options based on historical submissions.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function getTreasuryFormSubmissionFilterOptions({ signal } = {}) {
  return httpClient.get("/treasury/form-submissions/filter-options", { signal });
}

/**
 * Calculate proposed movements and pre-approval status for a submission without mutating state.
 * @param {number|string} submissionId
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function getTreasuryFormSubmissionProposedMovements(submissionId, { signal } = {}) {
  return httpClient.get(`/treasury/form-submissions/${submissionId}/proposed-movements`, { signal });
}

/**
 * Update the treasury period assigned to an in-review submission.
 * @param {number|string} submissionId
 * @param {number} periodId
 */
export async function updateTreasuryFormSubmissionPeriod(submissionId, periodId) {
  return httpClient.patch(`/treasury/form-submissions/${submissionId}/period`, { periodId });
}

/**
 * Approve submission and insert confirmed treasury movements.
 * @param {number|string} submissionId
 */
export async function approveTreasuryFormSubmission(submissionId) {
  return httpClient.post(`/treasury/form-submissions/${submissionId}/approve`, {});
}

/**
 * Cancel an in-review submission with a mandatory reason.
 * @param {number|string} submissionId
 * @param {Object} payload
 * @param {string} payload.reason
 */
export async function cancelTreasuryFormSubmission(submissionId, { reason }) {
  return httpClient.post(`/treasury/form-submissions/${submissionId}/cancel`, { reason });
}

/**
 * List periods in DRAFT status for linking with submissions.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
export async function listDraftTreasuryPeriods({ signal } = {}) {
  const response = await httpClient.get("/treasury/periods", { signal });
  const periods = unwrapList(response);
  return periods.filter((p) => String(p.status).toUpperCase() === "DRAFT");
}

/**
 * Get attachment content (Blob with headers) for a submission.
 * @param {number|string} submissionId
 * @param {number|string} attachmentId
 * @param {Object} [options]
 * @param {'inline'|'attachment'} [options.disposition='inline']
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ data: Blob, headers: Headers }>}
 */
export async function getTreasuryFormSubmissionAttachmentContent(
  submissionId,
  attachmentId,
  { disposition = "inline", signal } = {}
) {
  const query = buildQuery({
    disposition: disposition || "inline",
  });
  return httpClient.getBlobWithHeaders(
    `/treasury/form-submissions/${submissionId}/attachments/${attachmentId}/content${query}`,
    { signal }
  );
}
